/**
 * Language context for the TamerinTECH landing page.
 *
 * English and German each have their own URL. The path wins over any saved
 * preference so the prerendered HTML and the first client render match.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { translations, Translations } from './translations';
import { languageFromPath, type Language } from './localePath';

export type { Language };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

function fallbackLanguage(): Language {
  if (typeof window === 'undefined') return 'en';
  try {
    const saved = localStorage.getItem('tamerintech-language');
    if (saved === 'en' || saved === 'de') return saved;
  } catch {
    // Ignore storage failures and use the browser language.
  }
  return navigator.language.toLowerCase().startsWith('de') ? 'de' : 'en';
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation();
  const pathLanguage = languageFromPath(location.pathname);
  const [fallback, setFallback] = useState<Language>(fallbackLanguage);
  const language = pathLanguage ?? fallback;

  const setLanguage = (lang: Language) => {
    setFallback(lang);
    try {
      localStorage.setItem('tamerintech-language', lang);
    } catch {
      // The URL still carries the language when the visitor moves to / or /de.
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;
